import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { saveDonationAmountsItem } from '../utils/firebaseFunctions';
import Loader from './Loader';
import Sidebar from './Sidebar';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { firestore } from '../firebase.config'; // adjust path if needed
import {
    collection,
    getDocs,
    deleteDoc,
    doc,
    updateDoc, // 🆕 Import updateDoc
} from 'firebase/firestore';
import { Trash2, Edit } from 'lucide-react'; // 🆕 Import Edit icon

const DonationAmounts = () => {
    const [Price, setPrice] = useState('');
    const [Description, setDescription] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [field, setField] = useState(false);
    const [alertStatus, setAlertStatus] = useState('danger');
    const [msg, setMsg] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [sliderList, setSliderList] = useState([]); // List of all sliders

    // 🆕 State for edit modal
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentEditItem, setCurrentEditItem] = useState(null);
    const [editPrice, setEditPrice] = useState('');
    const [editDescription, setEditDescription] = useState('');

    const toggleSidebar = (state) => {
        setIsSidebarOpen(state);
    };

    const saveDetails = async () => {
        setIsLoading(true);
        if (!Price || !Description) {
            setField(true);
            setMsg("Required fields can't be empty");
            setAlertStatus("danger");
            toast.error("Required fields can't be empty");

            setTimeout(() => {
                setField(false);
                setIsLoading(false);
            }, 4000);
            return;
        }

        try {
            const id = `${Date.now()}`; // Generate the ID here!

            const data = {
                id: id,
                Price: Number(Price), // Ensure price is stored as a number
                Description,
            };
            await saveDonationAmountsItem(data); // Save data to Firestore
            setIsLoading(false);
            setField(true);
            setMsg("Details saved successfully");
            toast.success("Details saved successfully");
            clearData();
            setTimeout(() => {
                setField(false);
                fetchSliders(); // Refresh the list after saving
            }, 1000); // Shorter timeout for refreshing data
        } catch (error) {
            console.error("Error saving details:", error);
            toast.error("Failed to save details");
            setIsLoading(false);
        }
    };

    const clearData = () => {
        setPrice('');
        setDescription('');
    };

    // Fetch all slider items
    const fetchSliders = async () => {
        try {
            const querySnapshot = await getDocs(collection(firestore, 'DonationAmounts'));
            const sliders = [];
            querySnapshot.forEach((doc) => {
                sliders.push({ ...doc.data(), docId: doc.id });
            });
            setSliderList(sliders);
        } catch (error) {
            console.error("Error fetching sliders:", error);
        }
    };

    // Delete slider
    const handleDeleteSlider = async (docId) => {
         const confirmDelete = window.confirm("Are you sure you want to delete this  item? This action cannot be undone.");
        if (!confirmDelete) {
            return; // If the user cancels, stop the function
        }

        try {
            await deleteDoc(doc(firestore, 'DonationAmounts', docId)); // Corrected collection name
            toast.success("Deleted successfully");
            fetchSliders(); // Refresh list
        } catch (error) {
            console.error("Error deleting slider:", error);
            toast.error("Failed to delete");
        }
    };

    // 🆕 Open Edit Modal
    const handleEditClick = (item) => {
        setCurrentEditItem(item);
        setEditPrice(item.Price);
        setEditDescription(item.Description);
        setIsEditModalOpen(true);
    };

    // 🆕 Close Edit Modal
    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setCurrentEditItem(null);
        setEditPrice('');
        setEditDescription('');
    };

    // 🆕 Update details
    const handleUpdateDetails = async () => {
        if (!currentEditItem) return;

        setIsLoading(true);
        if (!editPrice || !editDescription) {
            toast.error("Required fields can't be empty");
            setIsLoading(false);
            return;
        }

        try {
            const docRef = doc(firestore, 'DonationAmounts', currentEditItem.docId); // Corrected collection name
            await updateDoc(docRef, {
                Price: Number(editPrice), // Ensure price is updated as a number
                Description: editDescription,
            });
            toast.success("Details updated successfully");
            setIsLoading(false);
            handleCloseEditModal();
            fetchSliders(); // Refresh the list after updating
        } catch (error) {
            console.error("Error updating details:", error);
            toast.error("Failed to update details");
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSliders();
    }, []);

    return (
        <div className='lg:flex h-screen bg-white'>
            <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
            <div className={`flex-1 p-1 lg:p-12 overflow-y-auto transition-all duration-300 lg:mt-2 mt-16 ${isSidebarOpen ? 'ml-56 p-0' : 'lg:ml-20 sm:ml-2 '}`}>
                <div className={`w-full max-w-4xl mx-auto bg-white shadow-xl rounded-2xl lg:p-8  ${isSidebarOpen ? ' p-1' : 'lg:p-8 p-8'}`}>
                    <h1 className={`lg:text-2xl  font-bold text-[#fcaf17] mb-6 ${isSidebarOpen ? 'text-[13px]' : ''}`}>Add Donation Type</h1>

                    {field && (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className={`p-3 rounded-lg text-center text-lg font-medium ${alertStatus === "danger" ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}
                        >
                            {msg}
                        </motion.p>
                    )}

                    <div className='lg:space-y-3 space-y-1'>
                        <div>
                            <label className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`} htmlFor='Price'>Price</label>
                            <input
                                id='Price'
                                type='number'
                                value={Price}
                                autoComplete='off'
                                placeholder='Enter Price'
                                onChange={(e) => setPrice(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none ${isSidebarOpen ? 'text-xs' : ''}`}
                            />
                        </div>
                        {/* Description */}
                        <div>
                            <label
                                className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''
                                    }`}
                                htmlFor="Description"
                            >
                                Description
                            </label>
                            <textarea
                                id="Description"
                                value={Description}
                                autoComplete="off"
                                placeholder="Enter Description"
                                onChange={(e) => setDescription(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none ${isSidebarOpen ? 'text-xs' : ''
                                    }`}
                            />
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-start">
                            <button
                                className="bg-blue-600 text-white lg:text-sm text-xs py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-300"
                                onClick={saveDetails}
                            >
                                {isLoading ? <Loader /> : 'Save '}
                            </button>
                        </div>

                    </div>
                </div>

                {/* --- Table Section - Conditional Rendering --- */}
                {sliderList.length > 0 ? (
                    <div className="mt-12 p-8 transform transition-all duration-300 hover:shadow-yellow-300/40">
                        <h2 className="lg:text-2xl text-xl font-extrabold mb-8 text-[#fcaf17] tracking-wide flex items-center gap-3 animate-fade-in-down">
                            <span>✨</span> Donation Amounts Type
                        </h2>

                        <div className="overflow-hidden border border-yellow-200  transform transition-all duration-300 hover:shadow-yellow-200/20">
                            <table className="min-w-full text-sm text-left text-gray-700">
                                <thead className="bg-gradient-to-r from-yellow-100 to-yellow-200/70 backdrop-blur-sm border-b border-yellow-300 text-[14px] uppercase font-bold tracking-wider text-orange-800 animate-slide-in-top">
                                    <tr>
                                        <th className="px-6 py-4">Sl. No.</th>
                                        <th className="px-6 py-4">Price</th>
                                        <th className="px-6 py-4">Description</th>
                                        <th className="px-6 py-4 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sliderList.map((item, index) => (
                                        <tr
                                            key={item.docId}
                                            className="group bg-white/70 transition-all duration-300 hover:bg-orange-50/70 border-b border-orange-100 last:border-b-0 animate-fade-in"
                                        >
                                            <td className="px-6 py-4 font-semibold text-gray-800 text-base">
                                                {index + 1}
                                            </td>
                                            <td className="px-6 py-4 text-gray-900 font-medium">{item.Price}</td>
                                            <td className="px-6 py-4 text-gray-600 text-sm leading-relaxed max-w-sm overflow-hidden text-ellipsis whitespace-nowrap">
                                                {item.Description}
                                            </td>

                                            <td className="px-6 py-4 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => handleEditClick(item)}
                                                        className="inline-flex items-center gap-2 text-blue-600 hover:text-white bg-blue-100 hover:bg-blue-500 px-4 py-2 rounded-full text-sm font-semibold shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
                                                        title="Edit Donation Amount"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteSlider(item.docId)}
                                                        className="inline-flex items-center gap-2 text-red-600 hover:text-white bg-red-100 hover:bg-red-500 px-4 py-2 rounded-full text-sm font-semibold shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2"
                                                        title="Remove Donation Amount"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        Remove
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="mt-12 p-8 text-center text-gray-500 text-lg italic bg-white border border-orange-100 rounded-xl shadow-sm">
                        No Donation Amount to display yet. Let's create one!
                    </div>
                )}

                {/* --- Edit Modal --- */}
                {isEditModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md mx-4"
                        >
                            <h2 className="text-2xl font-bold text-[#fcaf17] mb-6">Edit Donation Amount</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor='editPrice'>Price</label>
                                    <input
                                        id='editPrice'
                                        type='number'
                                        value={editPrice}
                                        autoComplete='off'
                                        onChange={(e) => setEditPrice(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor='editDescription'>Description</label>
                                    <textarea
                                        id='editDescription'
                                        value={editDescription}
                                        autoComplete='off'
                                        onChange={(e) => setEditDescription(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                                        rows="4"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={handleCloseEditModal}
                                    className="bg-gray-300 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-400 transition duration-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateDetails}
                                    className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition duration-300"
                                >
                                    {isLoading ? <Loader /> : 'Update'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}

                <style jsx>{`
                    @keyframes fade-in-down {
                        from {
                            opacity: 0;
                            transform: translateY(-20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @keyframes slide-in-top {
                        from {
                            opacity: 0;
                            transform: translateY(-10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @keyframes fade-in {
                        from {
                            opacity: 0;
                        }
                        to {
                            opacity: 1;
                        }
                    }

                    .animate-fade-in-down {
                        animation: fade-in-down 0.8s ease-out forwards;
                    }

                    .animate-slide-in-top {
                        animation: slide-in-top 0.6s ease-out forwards;
                    }

                    .animate-fade-in {
                        animation: fade-in 0.5s ease-out forwards;
                    }
                `}</style>
            </div >
        </div >
    );
};

export default DonationAmounts;